import { useState, useEffect, useCallback, Fragment } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox, Car, ExternalLink, ClipboardList, Wrench, X, UserCog, KeyRound, History, ChevronDown, ChevronRight } from "lucide-react";
import { AUTH_DISABLED } from "@/lib/constants";
import { usePermissions } from "@/lib/usePermissions";
import { useAuth } from "@/context/AuthContext";
import { formatDatum } from "@/lib/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const RAENGE = [
  "Director", "Captain", "Lieutenant", "Sergeant II", "Sergeant I",
  "Officer II", "Officer I", "Guard II", "Guard I", "Cadet",
];
const RANG_INDEX = RAENGE.reduce((a, r, i) => ({ ...a, [r]: i }), {});
const ACTION_STYLE = {
  erstellt: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  "geändert": "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
  "gelöscht": "text-red-400 border-red-500/40 bg-red-500/10",
  archiviert: "text-blue-400 border-blue-500/40 bg-blue-500/10",
  wiederhergestellt: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  "zurückgesetzt": "text-orange-400 border-orange-500/40 bg-orange-500/10",
};

function addMonths(iso, m) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const day = d.getDate();
  d.setMonth(d.getMonth() + m);
  if (d.getDate() < day) d.setDate(0);
  return d.toISOString().slice(0, 10);
}

const emptyVehicle = () => ({ fahrzeugtyp: "", kennzeichen: "", letzte_inspektion: "", bemerkung: "" });
const emptyOfficer = () => ({ name: "", rang: "", dienstnummer: "", laufzettel: "", fahrzeuge: [], suspendiert: false, gekuendigt: false, meldepflicht: false, melde_grund: "" });

function inspectionStatus(iso) {
  if (!iso) return "none";
  const next = new Date(iso);
  if (isNaN(next)) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  next.setHours(0, 0, 0, 0);
  const diffDays = Math.round((next - today) / 86400000);
  if (diffDays < 0) return "overdue";
  if (diffDays <= 14) return "soon";
  return "ok";
}

const VSTYLE = {
  overdue: { border: "border-red-500/50", bg: "bg-red-500/5", car: "text-red-400", badge: "bg-red-500/15 text-red-400 border-red-500/40", label: "Überfällig", date: "text-red-400" },
  soon: { border: "border-amber-500/50", bg: "bg-amber-500/5", car: "text-amber-400", badge: "bg-amber-500/15 text-amber-400 border-amber-500/40", label: "Bald fällig", date: "text-amber-400" },
  ok: { border: "border-zinc-800", bg: "bg-black/30", car: "text-primary", badge: "", label: "", date: "text-emerald-400" },
  none: { border: "border-zinc-800", bg: "bg-black/30", car: "text-primary", badge: "", label: "", date: "text-zinc-200" },
};

const STATUS_RANK = { overdue: 3, soon: 2, ok: 1, none: 0 };
function worstStatus(fahrzeuge) {
  let worst = "none";
  for (const f of fahrzeuge || []) {
    const s = inspectionStatus(f.naechste_inspektion);
    if (STATUS_RANK[s] > STATUS_RANK[worst]) worst = s;
  }
  return worst;
}

export default function Officers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(emptyOfficer());
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const { user } = useAuth();
  const { can } = usePermissions();
  const canEditVehicles = can("officer.vehicles.edit");
  const canSuspend = can("officer.suspend");
  const canCreateAccount = can("accounts.manage");
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [account, setAccount] = useState({ name: "", dienstnummer: "", rang: "", password: "" });
  const [tab, setTab] = useState("officer");
  const [onlineNames, setOnlineNames] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [clLoading, setClLoading] = useState(false);
  const [clNameFilter, setClNameFilter] = useState(null);
  const [clSearch, setClSearch] = useState("");
  const [clShowOld, setClShowOld] = useState(false);
  const [clRangeDays, setClRangeDays] = useState(3);
  const isTierA = can("roles.manage");

  const submitAccount = async () => {
    if (!account.name.trim()) { toast.error("Name erforderlich"); return; }
    if ((account.password || "").length < 6) { toast.error("Passwort: mind. 6 Zeichen"); return; }
    setAccountBusy(true);
    try {
      await api.post("/auth/accounts", account);
      toast.success("Account (Officer) erstellt");
      setAccountOpen(false);
      setAccount({ name: "", dienstnummer: "", rang: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setAccountBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/officers");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshEffect(load);

  useEffect(() => {
    const loadPresence = () => api.get("/auth/presence").then((r) => setOnlineNames(r.data.online || [])).catch(() => {});
    loadPresence();
    const id = setInterval(loadPresence, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (tab !== "verlauf" || !isTierA) return;
    setClLoading(true);
    api.get("/changelog").then((r) => setChangelog(r.data)).catch(() => {}).finally(() => setClLoading(false));
  }, [tab, isTierA]);

  const openCreate = () => {
    setEditing(null);
    setValues(emptyOfficer());
    setDialogOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setValues({
      name: o.name || "",
      rang: o.rang || "",
      dienstnummer: o.dienstnummer || "",
      laufzettel: o.laufzettel || "",
      fahrzeuge: (o.fahrzeuge || []).map((f) => ({ ...f })),
      suspendiert: !!o.suspendiert,
      gekuendigt: !!o.gekuendigt,
      meldepflicht: !!o.meldepflicht,
      melde_grund: o.melde_grund || "",
      melde_last_ack: o.melde_last_ack || "",
      melde_ack_count: o.melde_ack_count || 0,
    });
    setDialogOpen(true);
  };

  const setVehicle = (idx, key, val) =>
    setValues((v) => ({
      ...v,
      fahrzeuge: v.fahrzeuge.map((f, i) => (i === idx ? { ...f, [key]: val } : f)),
    }));
  const addVehicle = () => setValues((v) => ({ ...v, fahrzeuge: [...v.fahrzeuge, emptyVehicle()] }));
  const removeVehicle = (idx) => setValues((v) => ({ ...v, fahrzeuge: v.fahrzeuge.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: values.name,
        rang: values.rang,
        dienstnummer: values.dienstnummer,
        laufzettel: values.laufzettel,
        suspendiert: !!values.suspendiert,
        gekuendigt: !!values.gekuendigt,
        meldepflicht: !!values.meldepflicht,
        melde_grund: values.meldepflicht ? (values.melde_grund || "") : "",
        fahrzeuge: values.fahrzeuge.map((f) => ({
          fahrzeugtyp: f.fahrzeugtyp || "",
          kennzeichen: f.kennzeichen || "",
          letzte_inspektion: f.letzte_inspektion || "",
          bemerkung: f.bemerkung || "",
        })),
      };
      if (editing) {
        await api.put(`/officers/${editing.id}`, payload);
        toast.success("Officer aktualisiert");
      } else {
        await api.post("/officers", payload);
        toast.success("Officer angelegt");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/officers/${confirm.id}`);
      toast.success("Officer gelöscht");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) =>
    !search.trim() ? true : [r.name, r.dienstnummer, r.rang].some((v) => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  const sorted = [...filtered].sort((a, b) => {
    const ra = a.rang in RANG_INDEX ? RANG_INDEX[a.rang] : 999;
    const rb = b.rang in RANG_INDEX ? RANG_INDEX[b.rang] : 999;
    if (ra !== rb) return ra - rb;
    return (a.name || "").localeCompare(b.name || "");
  });

  const activeOfficers = sorted.filter((o) => !o.gekuendigt);
  const formerOfficers = sorted.filter((o) => o.gekuendigt);
  const displayOfficers = [...activeOfficers, ...formerOfficers];

  const clFiltered = changelog.filter((c) => {
    if (clNameFilter && c.by !== clNameFilter) return false;
    const q = clSearch.trim().toLowerCase();
    if (!q) return true;
    return [c.action, c.bereich, c.entity, c.changes, c.by]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  // Einträge älter als N Tage einklappen (Officer-Verlauf).
  const clCollapseActive = clRangeDays !== "all";
  const clCutoff = Date.now() - Number(clRangeDays) * 24 * 60 * 60 * 1000;
  const clTime = (c) => { const d = new Date(c.ts); return isNaN(d) ? null : d.getTime(); };
  const clRecent = clFiltered.filter((c) => { if (!clCollapseActive) return true; const t = clTime(c); return t === null || t >= clCutoff; });
  const clOld = clCollapseActive ? clFiltered.filter((c) => { const t = clTime(c); return t !== null && t < clCutoff; }) : [];

  const renderClRow = (c, dim = false) => (
    <TableRow key={c.id} data-testid="changelog-row" className={`border-zinc-800 hover:bg-zinc-900/50 ${dim ? "opacity-60" : ""}`}>
      <TableCell className="font-mono text-xs text-zinc-300 whitespace-nowrap">{c.ts ? new Date(c.ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
      <TableCell className="text-sm text-zinc-300">{c.bereich || "—"}</TableCell>
      <TableCell><Badge className={`rounded-sm border font-mono text-[10px] ${ACTION_STYLE[c.action] || "text-zinc-400 border-zinc-700 bg-zinc-800/40"}`}>{c.action}</Badge></TableCell>
      <TableCell className="text-sm max-w-xs truncate">
        {c.entity ? <span className="text-zinc-200 font-medium">{c.entity}</span> : <span className="text-zinc-600">—</span>}
      </TableCell>
      <TableCell className="text-xs text-zinc-400 max-w-md whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        {c.changes ? c.changes : <span className="text-zinc-600">—</span>}
      </TableCell>
      <TableCell className="font-mono text-xs text-zinc-400">
        {c.by ? (
          <button data-testid="changelog-by-btn" onClick={() => setClNameFilter(c.by)} className="text-zinc-300 hover:text-primary hover:underline transition-colors text-left" title="Nach diesem Bearbeiter filtern">{c.by}</button>
        ) : "—"}
      </TableCell>
    </TableRow>
  );

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Personal"
        title="Officer"
        description="Officer-Übersicht mit Laufzettel und Fahrzeugen."
        icon={UserCog}
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input data-testid="officer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Officer suchen…" className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
          </div>
          {canCreateAccount && (
            <Button data-testid="account-create-btn" onClick={() => setAccountOpen(true)} className="ml-auto rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
              <KeyRound className="h-4 w-4" /> Account erstellen
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 border-b border-zinc-800">
          <button data-testid="officer-tab-officer" onClick={() => setTab("officer")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "officer" ? "border-primary text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>Officer</button>
          {isTierA && (
            <button data-testid="officer-tab-verlauf" onClick={() => setTab("verlauf")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "verlauf" ? "border-primary text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}><History className="h-3.5 w-3.5" /> Verlauf</button>
          )}
        </div>

        <Dialog open={accountOpen} onOpenChange={setAccountOpen}>
          <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-zinc-100 flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Account erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-xs text-zinc-500">Der Account ist gleichzeitig ein Officer-Eintrag. Login erfolgt über den Namen.</p>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Name (Login)</Label>
                <Input data-testid="account-name" value={account.name} onChange={(e) => setAccount({ ...account, name: e.target.value })} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Dienstnummer</Label>
                  <Input data-testid="account-dn" value={account.dienstnummer} onChange={(e) => setAccount({ ...account, dienstnummer: e.target.value })} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Rang</Label>
                  <Select value={account.rang} onValueChange={(v) => setAccount({ ...account, rang: v })}>
                    <SelectTrigger data-testid="account-rang" className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary"><SelectValue placeholder="Rang wählen" /></SelectTrigger>
                    <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-h-72">
                      {RAENGE.map((r) => <SelectItem key={r} value={r} className="rounded-sm text-sm">{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Passwort</Label>
                <Input data-testid="account-password" type="password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
              </div>
              <Button data-testid="account-submit" onClick={submitAccount} disabled={accountBusy} className="w-full rounded-sm bg-primary hover:bg-blue-600 text-white">
                {accountBusy ? "Erstellen…" : "Account erstellen"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {tab === "officer" && (loading ? (
          <div className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] py-16 flex flex-col items-center gap-2 text-zinc-600">
            <Inbox className="h-8 w-8" />
            <span className="font-mono text-sm">Keine Officer angelegt</span>
          </div>
        ) : (
          <Accordion type="multiple" className="space-y-3">
            {displayOfficers.map((o, idx) => (
              <Fragment key={o.id}>
              {o.gekuendigt && (idx === 0 || !displayOfficers[idx - 1].gekuendigt) && (
                <h2 data-testid="ehemalige-kollegen-header" className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 pt-6 pb-1">Ehemalige Kollegen</h2>
              )}
              <AccordionItem value={o.id} data-testid="officer-row" className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
                <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-zinc-900/50 [&[data-state=open]]:border-b [&[data-state=open]]:border-zinc-800">
                  <div className="flex items-center gap-4 flex-1 text-left">
                    <div className={`h-9 w-9 grid place-items-center bg-zinc-800 rounded-sm font-mono text-xs text-zinc-300 shrink-0 ${o.gekuendigt ? "opacity-60" : ""}`}>
                      {(o.dienstnummer || o.name || "?").toString().slice(0, 2).toUpperCase()}
                    </div>
                    <span title={onlineNames.includes(o.name) ? "Eingeloggt" : "Ausgeloggt"} data-testid={`officer-status-${o.id}`} className={`h-2.5 w-2.5 rounded-full shrink-0 ${onlineNames.includes(o.name) ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)]" : "bg-red-500"}`} />
                    <span className="text-sm font-medium text-zinc-100">{o.name}</span>
                    {o.rang && (
                      <Badge className="rounded-sm border border-zinc-700 bg-zinc-800/60 text-zinc-300 font-mono text-[10px] uppercase tracking-wide">
                        {o.rang}
                      </Badge>
                    )}
                    {o.gekuendigt && (
                      <Badge data-testid={`officer-badge-gekuendigt-${o.id}`} className="rounded-sm border border-red-500/40 bg-red-500/10 text-red-400 font-mono text-[10px] uppercase tracking-wide">Gekündigt</Badge>
                    )}
                    {o.suspendiert && !o.gekuendigt && (
                      <Badge data-testid={`officer-badge-suspendiert-${o.id}`} className="rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-400 font-mono text-[10px] uppercase tracking-wide">Suspendiert</Badge>
                    )}
                    {o.meldepflicht && !o.gekuendigt && (
                      <Badge data-testid={`officer-badge-meldepflicht-${o.id}`} className="rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-400 font-mono text-[10px] uppercase tracking-wide">Meldepflicht</Badge>
                    )}
                    {(() => {
                      const ws = worstStatus(o.fahrzeuge);
                      if (ws === "overdue" || ws === "soon") {
                        const st = VSTYLE[ws];
                        return (
                          <Badge className={`rounded-sm border font-mono text-[10px] gap-1 ${st.badge}`}>
                            <Wrench className="h-3 w-3" /> Inspektion {ws === "overdue" ? "überfällig" : "bald"}
                          </Badge>
                        );
                      }
                      return null;
                    })()}
                    <Badge className="rounded-sm border border-primary/30 bg-primary/10 text-primary font-mono text-[11px] ml-auto mr-2">
                      {o.dienstnummer || "—"}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5 pt-4 space-y-5">
                  {/* Laufzettel */}
                  <div className="flex items-center gap-3 text-sm">
                    <ClipboardList className="h-4 w-4 text-zinc-500" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Laufzettel:</span>
                    {o.laufzettel && /^https?:\/\//i.test(o.laufzettel) ? (
                      <a href={o.laufzettel} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                        Öffnen <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-zinc-400">{o.laufzettel || "—"}</span>
                    )}
                  </div>

                  {/* Fahrzeuge */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                      <Car className="h-4 w-4" /> Fahrzeuge ({(o.fahrzeuge || []).length})
                    </div>
                    {(o.fahrzeuge || []).length === 0 ? (
                      <p className="text-sm text-zinc-600 pl-6">Keine Fahrzeuge hinterlegt.</p>
                    ) : (
                      <Accordion type="multiple" className="space-y-2">
                        {o.fahrzeuge.map((f, i) => {
                          const st = VSTYLE[inspectionStatus(f.naechste_inspektion)];
                          return (
                          <AccordionItem key={i} value={`${o.id}-v${i}`} className={`border rounded-sm ${st.border} ${st.bg}`}>
                            <AccordionTrigger className="px-4 py-2.5 hover:no-underline hover:bg-zinc-900/50">
                              <div className="flex items-center gap-3 text-left w-full">
                                <Car className={`h-4 w-4 ${st.car}`} />
                                <span className="text-sm text-zinc-200">{f.fahrzeugtyp || "Fahrzeug"}</span>
                                <span className="font-mono text-xs text-zinc-500">{f.kennzeichen}</span>
                                {st.label && (
                                  <Badge className={`rounded-sm border font-mono text-[10px] ml-auto mr-2 ${st.badge}`}>{st.label}</Badge>
                                )}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 pt-2">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <Info label="Fahrzeugtyp" value={f.fahrzeugtyp} />
                                <Info label="Kennzeichen" value={f.kennzeichen} mono />
                                <Info label="Letzte Inspektion" value={formatDatum(f.letzte_inspektion)} mono />
                                <Info label="Nächste Inspektion" value={f.naechste_inspektion ? formatDatum(f.naechste_inspektion) : "—"} mono accent={st.date} />
                                <div className="col-span-2">
                                  <Info label="Bemerkung" value={f.bemerkung} />
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </div>

                  {/* Schrankinhalt */}
                  <div className="flex gap-2 pt-1">
                    <Button data-testid="officer-edit" size="sm" variant="outline" onClick={() => openEdit(o)} className="rounded-sm border-zinc-700 gap-2">
                      <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                    </Button>
                    <Button data-testid="officer-delete" size="sm" variant="ghost" onClick={() => setConfirm(o)} className="rounded-sm text-red-400 hover:bg-red-500/10 gap-2">
                      <Trash2 className="h-3.5 w-3.5" /> Löschen
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
              </Fragment>
            ))}
          </Accordion>
        ))}
        {tab === "verlauf" && isTierA && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                data-testid="changelog-search"
                value={clSearch}
                onChange={(e) => setClSearch(e.target.value)}
                placeholder="Verlauf durchsuchen (z. B. gelöscht, Officer, Name)…"
                className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Einklappen ab</span>
              <select
                data-testid="changelog-range-select"
                value={clRangeDays}
                onChange={(e) => setClRangeDays(e.target.value === "all" ? "all" : Number(e.target.value))}
                className="h-9 rounded-sm border border-zinc-700 bg-transparent px-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option className="bg-[#0C0E12]" value={3}>3 Tage</option>
                <option className="bg-[#0C0E12]" value={7}>7 Tage</option>
                <option className="bg-[#0C0E12]" value={14}>14 Tage</option>
                <option className="bg-[#0C0E12]" value={30}>30 Tage</option>
                <option className="bg-[#0C0E12]" value={90}>90 Tage</option>
                <option className="bg-[#0C0E12]" value="all">Alles zeigen</option>
              </select>
            </div>
          </div>
          {clNameFilter && (
              <button data-testid="changelog-clear-filter" onClick={() => setClNameFilter(null)} className="flex items-center gap-2 h-9 px-3 rounded-sm border border-primary/50 bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors">
                Bearbeiter: {clNameFilter} <X className="h-4 w-4" />
              </button>
            )}
            {clLoading ? (
              <div className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</div>
            ) : clFiltered.length === 0 ? (
              <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] py-16 flex flex-col items-center gap-2 text-zinc-600">
                <Inbox className="h-8 w-8" />
                <span className="font-mono text-sm">Noch keine Aktionen protokolliert</span>
              </div>
            ) : (
              <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11">Zeitpunkt</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Bereich</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Aktion</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Eintrag</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Änderung</TableHead>
                      <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Bearbeiter</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clRecent.map((c) => renderClRow(c))}
                    {clOld.length > 0 && (
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableCell colSpan={6} className="p-0">
                          <button
                            data-testid="changelog-toggle-old"
                            onClick={() => setClShowOld((v) => !v)}
                            className="w-full flex items-center justify-center gap-2 py-3 text-xs font-mono uppercase tracking-wide text-zinc-400 hover:text-primary hover:bg-zinc-900/40 transition-colors"
                          >
                            {clShowOld ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {clShowOld
                              ? "Ältere Einträge ausblenden"
                              : `${clOld.length} ältere ${clOld.length === 1 ? "Eintrag" : "Einträge"} (älter als ${clRangeDays} Tage) anzeigen`}
                          </button>
                        </TableCell>
                      </TableRow>
                    )}
                    {clShowOld && clOld.map((c) => renderClRow(c, true))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100">
              {editing ? "Officer bearbeiten" : "Officer anlegen"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required>
                <Input data-testid="officer-name" value={values.name} onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))} required className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
              </Field>
              <Field label="Rang">
                <Select value={values.rang || ""} onValueChange={(v) => setValues((s) => ({ ...s, rang: v }))}>
                  <SelectTrigger data-testid="officer-rang" className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary"><SelectValue placeholder="Auswählen…" /></SelectTrigger>
                  <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
                    {RAENGE.map((r) => <SelectItem key={r} value={r} className="rounded-sm text-sm">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Dienstnummer">
                <Input data-testid="officer-dienstnummer" value={values.dienstnummer} onChange={(e) => setValues((v) => ({ ...v, dienstnummer: e.target.value }))} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
              </Field>
            </div>
            <Field label="Laufzettel (Link)">
              <Input data-testid="officer-laufzettel" value={values.laufzettel} onChange={(e) => setValues((v) => ({ ...v, laufzettel: e.target.value }))} placeholder="https://…" className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
            </Field>

            {editing && (
              <>
              <div className="flex flex-wrap items-center gap-6 border border-zinc-800 rounded-sm p-4 bg-black/20">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="officer-suspendiert" disabled={!canSuspend} checked={!!values.suspendiert} onChange={(e) => setValues((v) => ({ ...v, suspendiert: e.target.checked }))} className="h-4 w-4 accent-amber-500 cursor-pointer disabled:opacity-50" />
                  <span className="text-sm text-amber-300 font-medium">Suspendiert</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="officer-gekuendigt" disabled={!canSuspend} checked={!!values.gekuendigt} onChange={(e) => setValues((v) => ({ ...v, gekuendigt: e.target.checked }))} className="h-4 w-4 accent-red-500 cursor-pointer disabled:opacity-50" />
                  <span className="text-sm text-red-300 font-medium">Gekündigt</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="officer-meldepflicht" disabled={!canSuspend} checked={!!values.meldepflicht} onChange={(e) => setValues((v) => ({ ...v, meldepflicht: e.target.checked }))} className="h-4 w-4 accent-sky-500 cursor-pointer disabled:opacity-50" />
                  <span className="text-sm text-sky-300 font-medium">Meldepflicht</span>
                </label>
                {!canSuspend && <span className="text-xs text-zinc-500">Nur Leitung (Director – Sergeant II)</span>}
                {canSuspend && <span className="text-xs text-zinc-500">Sperrt Login &amp; erzwingt Logout, bis der Haken entfernt wird.</span>}
              </div>
              {values.meldepflicht && canSuspend && (
                <div className="border border-sky-500/20 rounded-sm p-4 bg-sky-500/5 space-y-2">
                  <label className="block text-xs font-bold uppercase tracking-[0.12em] text-sky-300">Meldegrund <span className="text-zinc-500 normal-case tracking-normal font-normal">(nur für die Leitung – der Officer sieht dies NICHT)</span></label>
                  <textarea
                    data-testid="officer-melde-grund"
                    disabled={!canSuspend}
                    value={values.melde_grund || ""}
                    onChange={(e) => setValues((v) => ({ ...v, melde_grund: e.target.value }))}
                    rows={2}
                    placeholder="Weshalb steht diese Person unter Meldepflicht?"
                    className="w-full bg-transparent border border-zinc-700 rounded-sm p-2 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:opacity-50"
                  />
                  {values.melde_last_ack && (
                    <div data-testid="officer-melde-lastack" className="font-mono text-[10px] text-zinc-500">
                      Zuletzt gemeldet (10-4): {new Date(values.melde_last_ack).toLocaleString("de-DE")}{values.melde_ack_count ? ` · ${values.melde_ack_count}× bestätigt` : ""}
                    </div>
                  )}
                </div>
              )}
              </>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500 flex items-center gap-2">
                  <Car className="h-4 w-4" /> Fahrzeuge
                </Label>
                {canEditVehicles && (
                  <Button type="button" size="sm" variant="outline" onClick={addVehicle} data-testid="officer-add-vehicle" className="rounded-sm border-zinc-700 gap-1.5 h-8">
                    <Plus className="h-3.5 w-3.5" /> Fahrzeug
                  </Button>
                )}
              </div>
              {!canEditVehicles && (
                <p className="text-xs text-amber-400/80" data-testid="officer-vehicles-locked">Keine Berechtigung zum Bearbeiten von Fahrzeugen.</p>
              )}
              {values.fahrzeuge.length === 0 && <p className="text-sm text-zinc-600">Noch keine Fahrzeuge hinzugefügt.</p>}
              {values.fahrzeuge.map((f, idx) => (
                <div key={idx} className="border border-zinc-800 rounded-sm p-4 space-y-3 relative bg-black/20">
                  {canEditVehicles && (
                    <button type="button" onClick={() => removeVehicle(idx)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400" data-testid={`officer-remove-vehicle-${idx}`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Fahrzeugtyp">
                      <Input disabled={!canEditVehicles} data-testid={`officer-vehicle-${idx}-typ`} value={f.fahrzeugtyp} onChange={(e) => setVehicle(idx, "fahrzeugtyp", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary disabled:opacity-60" />
                    </Field>
                    <Field label="Kennzeichen">
                      <Input disabled={!canEditVehicles} data-testid={`officer-vehicle-${idx}-kennzeichen`} value={f.kennzeichen} onChange={(e) => setVehicle(idx, "kennzeichen", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary disabled:opacity-60" />
                    </Field>
                    <Field label="Letzte Inspektion">
                      <Input disabled={!canEditVehicles} type="date" data-testid={`officer-vehicle-${idx}-inspektion`} value={f.letzte_inspektion} onChange={(e) => setVehicle(idx, "letzte_inspektion", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary disabled:opacity-60" />
                    </Field>
                    <Field label="Nächste Inspektion (+3 Mon.)">
                      <div className="h-10 flex items-center gap-2 px-3 border border-zinc-800 rounded-sm bg-zinc-900/40 font-mono text-sm text-amber-400">
                        <Wrench className="h-3.5 w-3.5" />
                        {f.letzte_inspektion ? formatDatum(addMonths(f.letzte_inspektion, 3)) : "—"}
                      </div>
                    </Field>
                  </div>
                  <Field label="Bemerkung">
                    <Textarea disabled={!canEditVehicles} data-testid={`officer-vehicle-${idx}-bemerkung`} value={f.bemerkung} onChange={(e) => setVehicle(idx, "bemerkung", e.target.value)} rows={2} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none disabled:opacity-60" />
                  </Field>
                </div>
              ))}
            </div>

            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm text-zinc-400">Abbrechen</Button>
              <Button type="submit" disabled={submitting} data-testid="officer-submit" className="rounded-sm bg-primary hover:bg-blue-600 text-white">{submitting ? "Speichern…" : "Speichern"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Officer löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Dieser Vorgang kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="officer-confirm" onClick={doDelete} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Info({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-600">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${accent || "text-zinc-200"} text-sm mt-0.5 whitespace-pre-wrap`}>
        {value || <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}
